import { useLocation } from 'react-router-dom'

import { FILTERS } from '@components/music/filter/utils/constants'
import {
  facetTermDisplayText,
  getAggregationCategory,
  isValidFilter,
} from '@components/music/filter/utils/helpers'
import { AggregationTerm } from '@components/music/filter/utils/types'
import { parseURL } from '@hooks/useUpdateQueryParams'
import { usePageApi } from '@providers/PageApiProvider'
import { capitalize } from '@utils/formatters/string.formatter'

/**
 * Dynamically creates a page title for the music and music search pages based on the current URL values
 *
 * For example:
 * * `/music/premium-content` displays "Premium Content Titles | Noteflight"
 * * `/music/intermediate?instruments=piano_keyboard` displays "Intermediate Titles for Piano/Keyboard | Noteflight"
 * * `/music/solo?genres=rock,wedding&instruments=accordion` displays "Solo Rock Wedding Titles for Accordion
 * Noteflight"
 * * `/music/pop?difficulty_level=20&instruments=piano_keyboard,voice&opening_key=Ab%2Fmajor` displays "Intermediate Pop
 * Titles for Piano/Keyboard, Voice in Ab Major | Noteflight"
 */
export default function useMusicPageTitle(): string {
  const location = useLocation()
  const { pageData } = usePageApi<any>()

  if (location.pathname === '/music') {
    return 'Music | Noteflight'
  }

  const adjectives: string[] = []
  const instruments: string[] = []
  let keySig = ''

  // Collects the terms into the appropriate lists
  function handleFilter(category, value): void {
    if (category === AggregationTerm.INSTRUMENTS) {
      instruments.push(value)
    } else if (category === AggregationTerm.DIFFICULTY) {
      adjectives.unshift(value)
    } else if (category === AggregationTerm.OPENING_KEY) {
      keySig = value
    } else {
      adjectives.push(value)
    }
  }

  // Parse info from slug
  if (pageData?.slug_filter) {
    const { category, value } = pageData.slug_filter
    const paramCategory = getAggregationCategory(category)
    const filter = FILTERS[paramCategory]
    handleFilter(
      paramCategory,
      facetTermDisplayText(paramCategory, value, filter?.translationPrefix)
    )
  }

  // Parse info from query
  const queryParams = parseURL(location.search) as any

  // Use search term as main title if it is present
  if (queryParams.term) {
    return `${queryParams.term[0].split(' ').map(capitalize).join(' ')} Titles | Noteflight`
  }

  for (const key of Object.keys(queryParams).filter((key) => isValidFilter(key, false))) {
    const paramCategory = getAggregationCategory(key)
    const filter = FILTERS[paramCategory]
    for (const value of queryParams[key]) {
      handleFilter(
        paramCategory,
        facetTermDisplayText(paramCategory, value, filter?.translationPrefix)
      )
    }
  }

  return `${adjectives.join(' ')} Titles ${
    instruments.length ? `for ${instruments.join(', ')} ` : ''
  }${keySig ? `in ${keySig} ` : ''}| Noteflight`.trim()
}
